import { useMemo } from 'react'
// Material
import { Divider } from '@mui/material'
import { decodeSubscriptionPermission } from '@weblite/permission'
import { Admin } from '@weblite/ts-action/subscription'
import { AddUserIcon } from '$common/icons'
import { MenuIcon } from '$common/icons/menu'
import { UserListItemSkeleton, UserListItem } from '$common/user-list-item'
import { EmptyStateList } from '$common/empty-state-list'
import { withSearchableListPage } from '$common/searchable-list-page'
import {
  useClientUserPermissions,
  useHallAdmins,
  useSearchedHallAdmins,
} from '$logic/subscription/react-hook'
import { showHallMemberMenu } from '$comp/modals/menus/hall-member-menu'
import {
  closeHallAdmins,
  openAddHallAdmin,
  openUserProfile,
} from '$logic/router'
import { getUserDisplayName, useUser } from '$logic/users'
import { t } from '$i18n'
import { useClientUser } from '$logic/client-user'

function ListItem({
  id,
  roomId,
  isClientUserOwner,
  promoterId,
  decodedPermissions,
}: Admin & {
  roomId: string
  isClientUserOwner: boolean
  decodedPermissions: ReturnType<typeof decodeSubscriptionPermission>
}) {
  const { user: promoter } = promoterId ? useUser(promoterId) : { user: null }
  const clientUserId = useClientUser()?.id
  const promotedByClientUser = !isClientUserOwner && promoterId === clientUserId
  return (
    <>
      <UserListItem
        userId={id}
        hasDivider={false}
        onClick={() => openUserProfile(id)}
        role={decodedPermissions?.isOwner ? t('common.owner') : null}
        secondaryText={
          promoter
            ? `${t('admins.promotedBy')} ${getUserDisplayName(promoter)}`
            : ''
        }
        SecondaryActionIcon={
          (isClientUserOwner && !decodedPermissions?.isOwner) ||
          promotedByClientUser
            ? MenuIcon
            : undefined
        }
        onSecondaryActionIconClick={() =>
          showHallMemberMenu({
            hallId: roomId,
            userId: id,
            isAdmin: true,
            isInAdminsPage: true,
            promotedByClientUser,
          })
        }
      />
      <Divider variant="inset" />
    </>
  )
}

function EmptyStateComponent() {
  return <EmptyStateList />
}

export function Admins({ roomId }: { roomId: string }) {
  const clientUserPermissions = useClientUserPermissions(roomId)

  const Page = useMemo(
    () =>
      withSearchableListPage({
        appBarTitle: t('admins.title'),
        ListItem: item => (
          <ListItem
            {...item}
            roomId={roomId}
            isClientUserOwner={clientUserPermissions?.isOwner}
          />
        ),
        SkeletonItem: UserListItemSkeleton,
        EmptyStateComponent,
        fetchHook: () => useHallAdmins(roomId, 'admins'),
        searchHook: useSearchedHallAdmins,
        actionButtonText: t('admins.addAdmin'),
        actionButtonIcon: <AddUserIcon color="secondary" />,
        onActionButtonClick: () => openAddHallAdmin(roomId),
        onBack: closeHallAdmins,
      }),
    [roomId, clientUserPermissions?.isOwner],
  )

  return (
    <Page
      fetchHookParams={roomId}
      getSearchHookParams={(query, items) => ({
        query,
        fetchedItems: items,
      })}
      hideActionButton={!clientUserPermissions?.addNewAdmins}
    />
  )
}
